import {
	Alert,
	Button,
	Divider,
	Icons,
	Inline,
	SelectInput,
	Spinner,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	isTruthy,
	SelectInputField,
	SubmissionError,
	SwitchInputField,
	useFieldValue,
	useForm,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { EmailComposerFields } from "./EmailComposerFields"
import {
	fetchComposeDefaults,
	joinEmails,
	postMailMessage,
	type TComposableModelType,
	type TComposeNewThread,
	type TComposeThread,
	type TMailComposeDefaults,
} from "./store"

type TComposeFormValues = {
	/** The thread to reply on; "" starts a new thread. */
	mail_thread_id: string
	mailbox_id: string
	to: string
	cc: string
	subject: string
	body: string
	include_signature: boolean
	include_original: boolean
	attachments: FileList | null
}

/**
 * Where the email starts from: replying to a message (on one of its
 * threads), or emailing about a Trip or lead (on one of its threads, or a
 * new one).
 */
type TComposeSource =
	| { messageId: number; modelType?: never; modelId?: never }
	| { messageId?: never; modelType: TComposableModelType; modelId: number }

/**
 * Opens the email composer: reply on a thread, or start a new one.
 */
export function ComposeEmail({
	title,
	onSuccess,
	children,
	...source
}: TComposeSource & {
	title: string
	onSuccess?: () => unknown
	children: (props: { onCompose: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ onCompose: open })}
			<Dialog open={isOpen} onClose={close} title={title} lg>
				<Dialog.Body>
					{isOpen ? (
						<ComposeEmailForm
							{...source}
							onCancel={close}
							onSent={() => {
								close()
								onSuccess?.()
							}}
						/>
					) : null}
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function ComposeEmailForm({
	onSent,
	onCancel,
	...source
}: TComposeSource & {
	onSent: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	// `modelType` is only ever set on the record source, so it narrows the
	// union both ways (a `messageId` check can't: 0 is falsy too).
	const params = source.modelType
		? { model_type: source.modelType, model_id: source.modelId }
		: { message_id: source.messageId }
	const { data: defaults, error } = useSWR<TMailComposeDefaults, Error>(
		["/mail-messages/compose", params],
		() => fetchComposeDefaults(xhr, params),
		{ revalidateOnFocus: false }
	)

	if (error) {
		return (
			<Alert status="warning">
				{error.message || "You can not send emails from any mailbox."}
			</Alert>
		)
	}

	if (!defaults) {
		return <Spinner padding="4" alignCenter />
	}

	const thread = defaults.threads.find(
		(thread) => thread.id === defaults.mail_thread_id
	)

	return (
		<Form<TComposeFormValues>
			initialValues={{
				...valuesFor(thread, defaults.new),
				body: "",
				include_signature: defaults.has_email_signature,
				include_original: true,
				attachments: null,
			}}
			onSubmit={withServerErrors(async (values) => {
				const message = {
					to: values.to,
					cc: values.cc || "",
					subject: values.subject,
					body: values.body,
					include_signature: isTruthy(values.include_signature)
						? (1 as const)
						: (0 as const),
					attachments: values.attachments ? Array.from(values.attachments) : [],
				}
				const replyOn = defaults.threads.find(
					(thread) => String(thread.id) === values.mail_thread_id
				)
				if (replyOn) {
					await postMailMessage(xhr, {
						...message,
						mail_thread_id: replyOn.id,
						reply_to_message_id: replyOn.reply_to_message_id,
						include_original: isTruthy(values.include_original) ? 1 : 0,
						timezone_offset: new Date().getTimezoneOffset(),
					})
				} else if (source.modelType) {
					await postMailMessage(xhr, {
						...message,
						model_type: source.modelType,
						model_id: source.modelId,
						mailbox_id: Number(values.mailbox_id),
					})
				}
				showSnackbar("Email queued for sending.")
				onSent()
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<ConversationFields defaults={defaults} disabled={submitting} />
						<EmailComposerFields
							hasEmailSignature={defaults.has_email_signature}
							maxAttachmentBytes={defaults.max_attachment_bytes}
							disabled={submitting}
						/>
						<OriginalMessageField disabled={submitting} />
						<Divider marginY="0" />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								<Icons.Mail /> {submitting ? "Sending..." : "Send Email"}
							</Button>
							<Button
								type="button"
								level="tertiary"
								onClick={onCancel}
								disabled={submitting}
							>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

/** The recipients, subject and mailbox a reply on the thread, or a new one, starts with. */
function valuesFor(
	thread: TComposeThread | undefined,
	newThread: TComposeNewThread | null
): Pick<
	TComposeFormValues,
	"mail_thread_id" | "mailbox_id" | "to" | "cc" | "subject"
> {
	if (thread) {
		return {
			mail_thread_id: String(thread.id),
			mailbox_id: String(thread.mailbox.id),
			to: joinEmails(thread.to),
			cc: joinEmails(thread.cc),
			subject: thread.reply_subject,
		}
	}
	return {
		mail_thread_id: "",
		mailbox_id: newThread ? String(newThread.mailbox.id) : "",
		to: joinEmails(newThread?.to ?? []),
		cc: joinEmails(newThread?.cc ?? []),
		subject: "",
	}
}

/**
 * The thread to reply on (when there is a choice) and the mailbox the email
 * goes out from: the thread's own, or one to pick for a new thread.
 */
function ConversationFields({
	defaults,
	disabled,
}: {
	defaults: TMailComposeDefaults
	disabled: boolean
}) {
	const form = useForm<TComposeFormValues>()
	const { value: threadId } = useFieldValue<string>("mail_thread_id")
	const thread = defaults.threads.find(
		(thread) => String(thread.id) === threadId
	)
	const hasChoice =
		defaults.threads.length > 1 ||
		(defaults.threads.length > 0 && defaults.new !== null)

	return (
		<Stack gap="4">
			{hasChoice ? (
				<Stack gap="1">
					<Text fontSize="sm" fontWeight="semibold">
						Conversation
					</Text>
					<SelectInput
						value={threadId}
						disabled={disabled}
						onChange={(e) => {
							const picked = defaults.threads.find(
								(thread) => String(thread.id) === e.currentTarget.value
							)
							const values = valuesFor(picked, defaults.new)
							form.batch(() => {
								for (const [name, value] of Object.entries(values)) {
									form.change(name as keyof typeof values, value)
								}
							})
						}}
					>
						{defaults.threads.map((thread) => (
							<option key={thread.id} value={thread.id}>
								{thread.subject || "(No Subject)"} · {thread.mailbox.name}
							</option>
						))}
						{defaults.new ? (
							<option value="">Start a new conversation</option>
						) : null}
					</SelectInput>
				</Stack>
			) : null}
			{thread || !defaults.new ? (
				<Stack gap="1">
					<Text fontSize="sm" fontWeight="semibold">
						Send From
					</Text>
					<Text color="muted">
						{thread ? `${thread.mailbox.name} <${thread.mailbox.email}>` : null}
					</Text>
				</Stack>
			) : (
				<SelectInputField
					name="mailbox_id"
					label="Send From"
					required
					disabled={disabled || defaults.new.mailboxes.length < 2}
				>
					{defaults.new.mailboxes.map((mailbox) => (
						<option key={mailbox.id} value={mailbox.id}>
							{mailbox.name} &lt;{mailbox.email}&gt;
						</option>
					))}
				</SelectInputField>
			)}
		</Stack>
	)
}

/** Quote the message being replied to; only for a reply on a thread. */
function OriginalMessageField({ disabled }: { disabled: boolean }) {
	const { value: threadId } = useFieldValue<string>("mail_thread_id")
	if (!threadId) {
		return null
	}
	return (
		<SwitchInputField
			name="include_original"
			label="Include the original message"
			help="Quotes the email you are replying to below your message, so everyone on it can follow the conversation."
			disabled={disabled}
		/>
	)
}
